import React, { useEffect, useState } from 'react';
import {
  Avatar, Spin, Tooltip, message,
} from 'antd';
import { EyeFilled, EyeOutlined, LoadingOutlined } from '@ant-design/icons';
import axios from 'axios';
import classNames from 'classnames';
import I18n from 'i18n-js';
import { func, number } from 'prop-types';
import { ticket_watch_path } from 'js-routes/generated/routes';

const MAX_VISIBLE_WATCHERS = 3;

const watchIcon = ({ watching, isSending }) => {
  if (isSending) return <LoadingOutlined />;

  return watching ? <EyeFilled /> : <EyeOutlined />;
};

const WatchTicketButton = ({ ticketId, onWatchingChange = (_watching) => {} }) => {
  const [watchState, setWatchState] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    let active = true;
    const loadWatchState = async () => {
      try {
        const { data } = await axios.get(ticket_watch_path(ticketId), {
          headers: { Accept: 'application/json' },
        });
        if (active) setWatchState(data);
      } catch (error) {
        if (active) console.error(error); // eslint-disable-line no-console
      } finally {
        if (active) setIsLoading(false);
      }
    };
    void loadWatchState();
    return () => {
      active = false;
    };
  }, [ticketId]);

  if (isLoading) {
    return (
      <span className="tickets-index-page__watchers">
        <Spin size="small" />
      </span>
    );
  }

  if (!watchState) return null;

  const { watching, watchers } = watchState;

  const toggleWatching = async () => {
    setIsSending(true);
    try {
      const { data } = await axios({
        method: watching ? 'delete' : 'post',
        url: ticket_watch_path(ticketId),
        headers: { Accept: 'application/json' },
        responseType: 'json',
      });
      setWatchState(data);
      onWatchingChange(data.watching);
    } catch {
      message.error(I18n.t('tickets.form.watching_failed'));
    } finally {
      setIsSending(false);
    }
  };

  const handleToggle = () => {
    void toggleWatching();
  };

  const label = I18n.t(watching ? 'tickets.form.unwatch_ticket' : 'tickets.form.watch_ticket');
  const tooltip = (
    <>
      <div>{label}</div>
      {watchers.map(({ id, type, name }) => (
        <div key={`${type}-${id}`} className="tickets-index-page__watcher-name">{name}</div>
      ))}
    </>
  );

  return (
    <span className="tickets-index-page__watchers">
      <Tooltip title={tooltip}>
        <button
          type="button"
          className={classNames(
            'tickets-index-page__watch-pill',
            { 'tickets-index-page__watch-pill--active': watching },
          )}
          aria-label={label}
          aria-pressed={watching}
          disabled={isSending}
          onClick={handleToggle}
        >
          <span className="tickets-index-page__watch-pill-icon">
            {watchIcon({ watching, isSending })}
          </span>
          <span className="tickets-index-page__watch-pill-label">
            {I18n.t(watching ? 'tickets.form.watching' : 'tickets.form.watch')}
          </span>
          {watchers.length > 0 && (
            <>
              <span className="tickets-index-page__watch-pill-divider" />
              <Avatar.Group
                size={20}
                max={{ count: MAX_VISIBLE_WATCHERS }}
                className="tickets-index-page__watch-pill-avatars"
              >
                {watchers.map(({
                  id, type, name, avatarUrl,
                }) => (
                  <Avatar key={`${type}-${id}`} src={avatarUrl} alt={name} />
                ))}
              </Avatar.Group>
            </>
          )}
        </button>
      </Tooltip>
    </span>
  );
};

WatchTicketButton.propTypes = {
  ticketId: number.isRequired,
  onWatchingChange: func,
};

export default WatchTicketButton;
